import React, { useState, useEffect, useCallback, useRef } from "react";
import { fetchTestimonials } from "../api";

const INTERVAL = 3500;

function useVisibleCount() {
  const getCount = useCallback(() => {
    const w = window.innerWidth;
    if (w < 640) return 1;
    if (w < 1024) return 2;
    return 3;
  }, []);
  const [count, setCount] = useState(getCount);
  useEffect(() => {
    const handle = () => setCount(getCount());
    window.addEventListener("resize", handle, { passive: true });
    return () => window.removeEventListener("resize", handle);
  }, [getCount]);
  return count;
}

// Skeleton card
function SkeletonCard() {
  return (
    <div className="bg-stone-800 border border-stone-700/60 rounded-2xl p-5 md:p-6 animate-pulse">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-full bg-stone-700" />
        <div className="flex-1 space-y-2">
          <div className="h-3 bg-stone-700 rounded w-24" />
          <div className="h-2 bg-stone-700 rounded w-16" />
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-3 bg-stone-700 rounded" />
        <div className="h-3 bg-stone-700 rounded w-5/6" />
        <div className="h-3 bg-stone-700 rounded w-4/6" />
      </div>
    </div>
  );
}

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const dragStart = useRef(null);
  const visibleCount = useVisibleCount();

  useEffect(() => {
    fetchTestimonials()
      .then((d) => setTestimonials(d.data || []))
      .catch(() => setTestimonials([]))
      .finally(() => setLoading(false));
  }, []);

  const total = testimonials.length;
  const maxIndex = Math.max(0, total - visibleCount);

  useEffect(() => {
    if (current > maxIndex) setCurrent(maxIndex);
  }, [maxIndex, current]);

  const goTo = useCallback(
    (idx) => {
      if (total === 0) return;
      const next = ((idx % total) + total) % total;
      setCurrent(next);
      setAnimKey((k) => k + 1);
    },
    [total],
  );

  const next = useCallback(() => goTo(current + 1), [goTo, current]);
  const prev = useCallback(() => goTo(current - 1), [goTo, current]);

  useEffect(() => {
    if (paused || total === 0) return;
    const t = setInterval(next, INTERVAL);
    return () => clearInterval(t);
  }, [next, paused, total]);

  const onPointerDown = (e) => {
    dragStart.current = e.clientX;
  };
  const onPointerUp = (e) => {
    if (dragStart.current === null) return;
    const delta = dragStart.current - e.clientX;
    if (Math.abs(delta) > 40) {
      setPaused(true);
      setTimeout(() => setPaused(false), 5000);
      delta > 0 ? next() : prev();
    }
    dragStart.current = null;
  };

  const visible =
    total > 0
      ? Array.from(
          { length: visibleCount },
          (_, i) => testimonials[(current + i) % total],
        )
      : [];
  const dotCount = maxIndex + 1;
  const activeDot = current % (dotCount || 1);

  return (
    <section
      className="bg-[#101010] py-14 md:py-20 px-4 sm:px-8 md:px-12 lg:px-20"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-end justify-between mb-10 md:mb-12">
          <div>
            <span className="inline-block font-body text-[10px] tracking-[0.25em] uppercase text-[#D29D4E] border border-[#D29D4E] px-3 py-1 rounded-full mb-4">
              Testimonials
            </span>
            <h2
              className="font-display font-normal text-white leading-tight"
              style={{ fontSize: "clamp(1.8rem, 4vw, 2.8rem)" }}
            >
              What Our Client Says
            </h2>
          </div>
          {!loading && total > 0 && (
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={() => {
                  prev();
                  setPaused(true);
                  setTimeout(() => setPaused(false), 5000);
                }}
                className="w-10 h-10 rounded-full border border-stone-700 hover:border-gold hover:bg-gold text-white/70 hover:text-white flex items-center justify-center transition-all duration-300"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                onClick={() => {
                  next();
                  setPaused(true);
                  setTimeout(() => setPaused(false), 5000);
                }}
                className="w-10 h-10 rounded-full border border-stone-700 hover:border-gold hover:bg-gold text-white/70 hover:text-white flex items-center justify-center transition-all duration-300"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Cards */}
        {loading ? (
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: `repeat(${visibleCount}, minmax(0, 1fr))`,
            }}
          >
            {[...Array(visibleCount)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : total === 0 ? null : (
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: `repeat(${visibleCount}, minmax(0, 1fr))`,
            }}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerLeave={() => {
              dragStart.current = null;
            }}
          >
            {visible.map((t, i) => (
              <div
                key={`${animKey}-${i}-${t._id}`}
                className="bg-stone-800 border border-stone-700/60 rounded-2xl p-5 md:p-6 cursor-grab active:cursor-grabbing"
                style={{
                  animation: "fadeUp 0.4s ease both",
                  animationDelay: `${i * 55}ms`,
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  {/* Avatar initial */}
                  <div className="w-9 h-9 rounded-full bg-gold/20 border border-gold/30 flex items-center justify-center flex-shrink-0">
                    <span className="font-body text-gold text-sm font-medium">
                      {t.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-body text-[#D29D4E] text-sm font-medium leading-tight">
                      {t.name}
                    </p>
                    <p className="font-body text-stone-400 text-[11px] mt-0.5">
                      {t.role}
                    </p>
                  </div>
                </div>
                <p className="font-body text-stone-400 text-sm leading-relaxed">
                  {t.text}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Progress + dots */}
        {!loading && total > 0 && (
          <div className="mt-8 flex flex-col items-center gap-4">
            <div className="w-20 h-0.5 bg-stone-700 rounded-full overflow-hidden">
              <div
                key={`bar-${animKey}`}
                className="h-full bg-stone-400 rounded-full"
                style={{
                  animation: paused
                    ? "none"
                    : `grow ${INTERVAL}ms linear forwards`,
                }}
              />
            </div>
            <div className="flex gap-2">
              {Array.from({ length: dotCount }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    goTo(i);
                    setPaused(true);
                    setTimeout(() => setPaused(false), 5000);
                  }}
                  className={`h-1 rounded-full transition-all duration-300 ${i === activeDot ? "w-7 bg-white" : "w-2 bg-stone-600 hover:bg-stone-400"}`}
                />
              ))}
            </div>
            <div className="flex gap-3 md:hidden">
              <button
                onClick={() => {
                  prev();
                  setPaused(true);
                  setTimeout(() => setPaused(false), 5000);
                }}
                className="w-10 h-10 rounded-full border border-stone-700 hover:bg-gold text-white/70 hover:text-white flex items-center justify-center transition-all duration-300"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                onClick={() => {
                  next();
                  setPaused(true);
                  setTimeout(() => setPaused(false), 5000);
                }}
                className="w-10 h-10 rounded-full border border-stone-700 hover:bg-gold text-white/70 hover:text-white flex items-center justify-center transition-all duration-300"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
        @keyframes grow   { from { width:0; } to { width:100%; } }
      `}</style>
    </section>
  );
}
